/**
 * The Pilotage Plans library screen. A "New Plan" button plus a scrollable list
 * of every saved plan, newest first: each row shows a small chart-area thumbnail
 * (projected from the plan's own geometry), the plan name, its last-edited date,
 * and a one-line summary of what's in it. Tapping a row loads that plan onto the
 * chart with all its layers visible and fits the view to it. A row's actions
 * (rename, duplicate, delete) reveal on a long-press or via the "⋯" button, so
 * the row itself stays a single clean tap-to-load.
 *
 * The plan bundle, its CRUD and persistence all live in the planner store; this
 * screen is just the list and the load hand-off. Building a plan is Stage 3.
 */
import { useRef, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { usePilotagePlannerStore } from '@/store/pilotagePlanner';
import { planBounds, type PilotagePlanDoc } from '@/pilotage/plannerModel';
import { planThumbnail, planSummaryLine, formatPlanDate } from '@/pilotage/plannerView';
import { Sheet } from './Sheet';

/** The mini chart-area preview, drawn straight from the plan's projected geometry. */
function PlanThumb({ doc }: { doc: PilotagePlanDoc }) {
  const t = planThumbnail(doc, 88, 60, 6);
  if (!t) {
    return (
      <div className="acp-plan-thumb acp-plan-thumb-empty" aria-hidden="true">
        <span>—</span>
      </div>
    );
  }
  return (
    <svg
      className="acp-plan-thumb"
      viewBox={`0 0 ${t.width} ${t.height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {t.strokes.map((s, i) => (
        <polyline
          key={`s${i}`}
          points={s.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={1.6}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {t.dots.map(([x, y], i) => (
        <circle key={`d${i}`} cx={x.toFixed(1)} cy={y.toFixed(1)} r={1.9} fill="var(--accent)" />
      ))}
    </svg>
  );
}

export function PilotagePlansSheet() {
  const plans = usePilotagePlannerStore((s) => s.plans);
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const newPlan = usePilotagePlannerStore((s) => s.newPlan);
  const renamePlan = usePilotagePlannerStore((s) => s.renamePlan);
  const duplicatePlan = usePilotagePlannerStore((s) => s.duplicatePlan);
  const deletePlan = usePilotagePlannerStore((s) => s.deletePlan);
  const loadPlan = usePilotagePlannerStore((s) => s.loadPlan);

  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const fitBoundsTo = useShellStore((s) => s.fitBoundsTo);

  // Which row's action drawer is open, and the live rename text for it.
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  // Long-press reveals a row's actions without eating the tap-to-load: the press
  // timer sets a suppress flag the row's click then honours once.
  const pressTimer = useRef<number | null>(null);
  const suppressClick = useRef(false);

  // Newest first, so the plan we were last in is at the top.
  const sorted = [...plans].sort((a, b) => b.updatedMs - a.updatedMs);

  function createPlan() {
    newPlan();
    closeSheet();
    showToast('New plan — pick a tool to start building');
  }

  function load(doc: PilotagePlanDoc) {
    loadPlan(doc.id);
    const b = doc.bounds ?? planBounds(doc);
    if (b) fitBoundsTo(b);
    closeSheet();
    showToast(`Loaded ${doc.name}`);
  }

  function toggleActions(id: string, name: string) {
    setOpenActionsId((cur) => (cur === id ? null : id));
    setRenameDraft(name);
  }

  function startPress(id: string, name: string) {
    suppressClick.current = false;
    pressTimer.current = window.setTimeout(() => {
      suppressClick.current = true;
      setOpenActionsId(id);
      setRenameDraft(name);
    }, 480);
  }
  function endPress() {
    if (pressTimer.current != null) window.clearTimeout(pressTimer.current);
    pressTimer.current = null;
  }

  function onRowClick(doc: PilotagePlanDoc) {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    load(doc);
  }

  function duplicate(id: string) {
    const copyId = duplicatePlan(id);
    setOpenActionsId(null);
    showToast(copyId ? 'Plan duplicated' : 'Could not duplicate');
  }

  function remove(doc: PilotagePlanDoc) {
    deletePlan(doc.id);
    if (openActionsId === doc.id) setOpenActionsId(null);
    showToast(`Deleted ${doc.name}`);
  }

  return (
    <Sheet
      name="pilotageplans"
      title="Pilotage Plans"
      sub="Build, save and reload detailed plans for tricky harbour approaches, rivers and close-quarters work. Tap a plan to load it onto the chart; long-press (or ⋯) to rename, duplicate or delete."
    >
      <div className="acp-btn-row" style={{ marginTop: 4 }}>
        <button type="button" className="acp-btn primary full" onClick={createPlan}>
          + New Plan
        </button>
      </div>

      {sorted.length === 0 ? (
        <p className="acp-empty">
          No plans yet. Tap New Plan to start one, then drop a route, bearings, contour lines and
          notes on the chart. It all saves as one plan you can reload any time.
        </p>
      ) : (
        <div className="acp-plan-list">
          {sorted.map((doc) => {
            const active = doc.id === activePlanId;
            const actionsOpen = openActionsId === doc.id;
            return (
              <div className={`acp-plan-row${active ? ' active' : ''}`} key={doc.id}>
                <div className="acp-plan-main">
                  <button
                    type="button"
                    className="acp-plan-open"
                    onClick={() => onRowClick(doc)}
                    onPointerDown={() => startPress(doc.id, doc.name)}
                    onPointerUp={endPress}
                    onPointerLeave={endPress}
                    onPointerCancel={endPress}
                  >
                    <PlanThumb doc={doc} />
                    <span className="acp-plan-meta">
                      <b>
                        {doc.name}
                        {active ? '  ✓' : ''}
                      </b>
                      <span className="acp-plan-when">{formatPlanDate(doc.updatedMs)}</span>
                      <span className="acp-plan-sum">{planSummaryLine(doc)}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="acp-plan-more"
                    aria-label={`Actions for ${doc.name}`}
                    aria-expanded={actionsOpen}
                    onClick={() => toggleActions(doc.id, doc.name)}
                  >
                    ⋯
                  </button>
                </div>

                {actionsOpen && (
                  <div className="acp-plan-actions">
                    <input
                      className="acp-text-input"
                      value={renameDraft}
                      onChange={(e) => {
                        setRenameDraft(e.target.value);
                        renamePlan(doc.id, e.target.value);
                      }}
                      placeholder="Plan name"
                      aria-label={`Rename ${doc.name}`}
                    />
                    <div className="acp-btn-row" style={{ marginTop: 8 }}>
                      <button type="button" className="acp-btn" onClick={() => duplicate(doc.id)}>
                        Duplicate
                      </button>
                      <button type="button" className="acp-btn" onClick={() => remove(doc)}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
